import React from "react";

export function AuditPage() {
  return (
    <div className="page route-enter">
      <div className="page-header" style={{flexDirection:"column", alignItems:"flex-start", gap:8}}>
        <div className="row" style={{width:"100%", alignItems:"flex-start"}}>
          <div className="h-eyebrow">Design brief · v1</div>
          <div className="spacer"/>
          <div className="pill">v1 · {new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"})}</div>
        </div>
        <h1 className="page-title" style={{marginTop:6}}>An audit of what <em>is</em>, and where we're taking it.</h1>
        <p className="page-sub" style={{maxWidth:720, marginTop:6}}>
          One-pass critique of the current Venzyme.LIFE interface, followed by a deliberate direction for the redesign. Every screen in this prototype is built from the system below — palette, type, density, layout — exposed as live Tweaks.
        </p>
      </div>

      <div className="grid grid--2" style={{gap: 32, alignItems: "start"}}>
        <div className="stack">
          <div className="rule">01 · The problem</div>
          <div className="card">
            <p style={{fontSize:"var(--text-lg)", lineHeight:1.55, marginBottom:16}}>
              The current app <em style={{color:"var(--accent-hi)"}}>works</em> but reads like a tool built by engineers for themselves. Green-on-white buttons, emoji icons, uniform card grids, and a navy admin header lifted from the 2015 dashboard template. None of it says <em>prestige</em>, and none of it says <em>life sciences</em>.
            </p>
            <ul style={{paddingLeft:20, lineHeight:1.7, color:"var(--text-mute)"}}>
              <li>No brand system — colors picked per-page, not from a palette.</li>
              <li>No typographic voice — system stack with no display face.</li>
              <li>Emoji (📤 📊 ⚙️ 📁) where iconography should carry weight.</li>
              <li>Dashboard greets the user but doesn't <em>orient</em> them; stats are hard-coded zeros.</li>
              <li>Admin is visually separate from the product — looks like a different app.</li>
              <li>No data density controls — everything is padded the same.</li>
            </ul>
          </div>
        </div>

        <div className="stack">
          <div className="rule">02 · The direction</div>
          <div className="card" style={{borderColor:"var(--line-accent)"}}>
            <div className="h-eyebrow" style={{marginBottom:8}}>Positioning</div>
            <h2 className="h-display" style={{fontSize:"var(--text-2xl)", marginBottom:12}}>
              Bench meets boardroom.
            </h2>
            <p style={{color:"var(--text-mute)", marginBottom:16}}>
              Venzyme sits at an unusual intersection: AI-driven evaluation of early-stage life-sciences companies. The interface should feel like the <em>instrument panel</em> of a research institution and the <em>deal memo</em> of a top-tier fund — both at once.
            </p>
            <div className="grid grid--2" style={{gap:12, marginTop:8}}>
              <div>
                <div className="h-eyebrow">Feels like</div>
                <div style={{fontSize:"var(--text-sm)", marginTop:4, lineHeight:1.55}}>FT · Bloomberg Terminal · <em>Nature</em> · Arc · a Keithley lab meter</div>
              </div>
              <div>
                <div className="h-eyebrow">Not like</div>
                <div style={{fontSize:"var(--text-sm)", marginTop:4, lineHeight:1.55}}>Stripe dashboards · generic SaaS · crypto apps · Material Design</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <hr className="hr" style={{margin:"48px 0"}}/>

      <div className="rule" style={{marginBottom:24}}>03 · The system</div>
      <div className="grid grid--3" style={{gap:24}}>
        <div className="card">
          <div className="h-eyebrow">Palette</div>
          <h3 className="h-display" style={{fontSize:"var(--text-xl)", margin:"4px 0 14px"}}>Oxidized, not neon.</h3>
          <p className="dim" style={{fontSize:"var(--text-sm)", marginBottom:14}}>Default accent is <em>verdigris</em> — the patina of aged copper. Grounded in science, not startup.</p>
          <div style={{display:"flex", gap:8, marginBottom:10}}>
            {["#4A8F7C","#62B39A","#D58A3B","#0D2620","#E8E6DF","#6FA37A","#BD5A4E"].map(c=>(
              <div key={c} style={{width:36, height:36, borderRadius:4, background:c, border:"1px solid var(--line)"}}/>
            ))}
          </div>
          <div className="as-of">Alternatives: ultramarine · cinnabar · graphite</div>
        </div>

        <div className="card">
          <div className="h-eyebrow">Typography</div>
          <h3 className="h-display" style={{fontSize:"var(--text-xl)", margin:"4px 0 14px"}}>Fraunces <em>and</em> Inter.</h3>
          <div style={{fontFamily:"var(--ff-h)", fontSize:46, lineHeight:1, fontVariationSettings:'"opsz" 144', letterSpacing:"-0.03em"}}>Aa <em style={{fontStyle:"italic", color:"var(--accent-hi)"}}>Bb</em></div>
          <div style={{fontFamily:"var(--ff-sans)", fontSize:15, color:"var(--text-mute)", marginTop:10}}>The quick brown fox jumps over the lazy dog.</div>
          <div style={{fontFamily:"var(--ff-mono)", fontSize:12, color:"var(--text-dim)", marginTop:4, letterSpacing:".1em"}}>0123456789 · USD 1,240,000</div>
        </div>

        <div className="card">
          <div className="h-eyebrow">Motifs</div>
          <h3 className="h-display" style={{fontSize:"var(--text-xl)", margin:"4px 0 14px"}}>Instrument chrome.</h3>
          <ul style={{paddingLeft:16, lineHeight:1.8, fontSize:"var(--text-sm)", color:"var(--text-mute)"}}>
            <li>Tabular mono numerals for every metric.</li>
            <li>Small-caps rules between sections (like lab notebooks).</li>
            <li>Hairline 1px borders; minimal radii (2–6px).</li>
            <li>Dark as default; light as a peer, not a downgrade.</li>
          </ul>
        </div>
      </div>

      <hr className="hr" style={{margin:"48px 0"}}/>

      <div className="rule" style={{marginBottom:16}}>04 · What's inside this prototype</div>
      <div className="grid grid--4" style={{gap:16}}>
        {[
          ["Dashboard","Two layouts — desk & overview"],
          ["Analyzer","Multi-step upload + extraction"],
          ["Analysis view","IAS, radar, missing parts, notes"],
          ["Pipeline","Triage table + kanban"],
          ["Landing","Hero, positioning, how-it-works"],
          ["Auth","Sign in · register"],
          ["Admin","Overview, users, analytics, settings"],
          ["Tweaks","Live palette / density / type / layout"],
        ].map(([t,d]) => (
          <div key={t} className="card" style={{padding:"16px 18px"}}>
            <div style={{fontSize:"var(--text-md)", fontWeight:500}}>{t}</div>
            <div className="dim" style={{fontSize:"var(--text-xs)", marginTop:4}}>{d}</div>
          </div>
        ))}
      </div>

      <hr className="hr" style={{margin:"48px 0"}}/>

      <div className="rule" style={{marginBottom:24}}>05 · Tell me if I'm off</div>
      <div className="card" style={{borderColor:"var(--line-accent)", background:"var(--surface-2)"}}>
        <p style={{fontSize:"var(--text-lg)", lineHeight:1.55}}>
          Open the <strong style={{color:"var(--accent-hi)"}}>Tweaks</strong> panel (toggle in the toolbar) to try alternate palettes, switch light/dark, change density, swap the display face between editorial Fraunces and technical mono, or collapse the sidebar to a rail. If any of those feel closer to right, say the word and I'll lock it in before moving further.
        </p>
      </div>
    </div>
  );
}
